import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api/client';
import { printReceipt } from '../../lib/store/receipt';

interface OrderLine { name: string; quantity: number; unitCents: number }
interface Tracking { carrier: string; trackingNumber: string; url?: string; shippedOn: string }
interface Order {
  orderNum: string;
  status: string;
  createdOn?: string;
  items: OrderLine[];
  /* Already returned by GET /orders — the endpoint hands back the whole record — but not previously
     declared here, because the list only rendered the total. The receipt needs the breakdown. */
  subtotalCents: number;
  shippingCents: number;
  taxCents?: number;
  totalCents: number;
  contactEmail?: string;
  shippingService?: string;
  shippingAddress?: {
    name?: string; phone?: string; line1?: string; line2?: string;
    city?: string; state?: string; postalCode?: string; country?: string;
  };
  /** Present only when a promotion was applied at Stripe Checkout, in cents to subtract. */
  discountCents?: number;
  /** The code the customer typed. Shown back to them so a total lower than the items is explained. */
  promotionCode?: string;
  tracking?: Tracking;
}

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const STATUS_TONE: Record<string, string> = {
  PENDING: 'pend', PAID: 'ok', FULFILLING: 'ok', SHIPPED: 'ship', DELIVERED: 'ship', CANCELLED: 'bad',
};

export function Orders() {
  /* Set when the browser refused the print tab — a blocked pop-up is the one failure a user cannot
     diagnose from a screen that simply did not change. */
  const [blocked, setBlocked] = useState<string | null>(null);
  const [params] = useSearchParams();
  const highlight = params.get('order');
  const qc = useQueryClient();
  const orders = useQuery({ queryKey: ['orders'], queryFn: () => api.get<Order[]>('/orders') });

  // Discard a still-PENDING order (a checkout that was never completed).
  const cancel = useMutation({
    mutationFn: (orderNum: string) => api.post(`/orders/${orderNum}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Orders</h1>
          <div className="sub">Your order history and shipment tracking.</div>
        </div>
      </div>

      {highlight && (
        <div className="banner-ok">✓ Thanks! Order <strong>{highlight}</strong> was placed. It will appear below once payment is confirmed.</div>
      )}

      {orders.isLoading && <p className="loading-screen">// loading orders…</p>}
      {orders.data && orders.data.length === 0 && (
        <div className="panel empty">
          <h3>No orders yet</h3>
          <p>When you purchase from the Store, your orders and tracking will show up here.</p>
        </div>
      )}

      <div className="orders">
        {orders.data?.map((o) => (
          <div className={`order-card${o.orderNum === highlight ? ' is-highlight' : ''}`} key={o.orderNum}>
            <div className="order-top">
              <div>
                <span className="mono order-num">{o.orderNum}</span>
                {o.createdOn && <span className="order-date">{new Date(o.createdOn).toLocaleDateString()}</span>}
              </div>
              <span className={`status-badge tone-${STATUS_TONE[o.status] ?? 'pend'}`}>{o.status}</span>
            </div>
            <ul className="order-items">
              {o.items.map((li, i) => (
                <li key={i}>
                  <span>{li.quantity}× {li.name}</span>
                  <span className="mono">{money(li.unitCents * li.quantity)}</span>
                </li>
              ))}
            </ul>
            {/*
              * A discount is shown as its own line rather than folded into the total. The items above are
              * listed at full price, so a total that silently disagreed with them reads as a bug — and this
              * is also the only place the customer can see which code was applied.
              */}
            {!!o.discountCents && (
              <div className="order-discount">
                <span>Discount{o.promotionCode ? ` · ${o.promotionCode}` : ''}</span>
                <span className="mono">−{money(o.discountCents)}</span>
              </div>
            )}
            <div className="order-bottom">
              <span className="order-total">Total <strong>{money(o.totalCents)}</strong></span>
              {/* Opens a print-ready slip; the browser's own Save-as-PDF makes the file. */}
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { if (!printReceipt(o)) setBlocked(o.orderNum); }}
              >Receipt</button>
              {blocked === o.orderNum && (
                <span className="cart-error">Allow pop-ups for this site to print the receipt.</span>
              )}
              {o.status === 'PENDING' ? (
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={cancel.isPending}
                  onClick={() => { if (confirm(`Discard the unfinished order ${o.orderNum}?`)) cancel.mutate(o.orderNum); }}
                >
                  {cancel.isPending ? 'Canceling…' : 'Cancel order'}
                </button>
              ) : o.tracking ? (
                <span className="tracking">
                  📦 {o.tracking.carrier} ·{' '}
                  {o.tracking.url ? (
                    <a href={o.tracking.url} target="_blank" rel="noreferrer" className="mono track-link">
                      {o.tracking.trackingNumber}
                    </a>
                  ) : (
                    <span className="mono">{o.tracking.trackingNumber}</span>
                  )}
                </span>
              ) : (
                <span className="tracking muted">Not yet shipped</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
